import { MCP_SCHEMA_VERSION } from '@fos/shared/contracts/mcp';
import { Copy, Plug, Plus, Terminal } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { Button } from '../../../ui/Button';
import { Mono } from '../../../ui/Mono';
import { SectionHeader } from '../../../ui/PageHeader';
import { Section } from '../../../ui/Section';
import { toast } from '../../../ui/Toast';
import { TOUCH_BUTTON, TOUCH_ICON } from '../../mobile';
import { SettingsHeader } from '../pages/SettingsLayout';
import { NewTokenDialog } from './NewTokenDialog';
import { PersonalTokensSection } from './PersonalTokensSection';
import { connectCommand, mcpBaseUrl } from './tokens-ui';

/**
 * Ustawienia → Tokeny i agenci (MVP §12, Etap 7; czeka na projekt w Figmie — wzorce z Figmy 12 Ustawienia):
 * tokeny osobiste MCP (lista, nowy token pokazany raz, cofnięcie) i instrukcja podłączenia Claude Code.
 * Sekcję runnerów dokłada moduł Agenci (slot `children` — renderowany pod tokenami).
 */
export function TokensSettingsPage({ children }: { children?: ReactNode }) {
  const [creating, setCreating] = useState(false);
  return (
    <div>
      <SettingsHeader
        title="Tokeny i agenci"
        description="Dostęp Claude Code i innych agentów MCP do Fravity OS — z Twoimi uprawnieniami, zawężonymi do zakresu tokenu."
        actions={
          <Button variant="primary" icon={Plus} onClick={() => setCreating(true)} className={TOUCH_BUTTON}>
            Nowy token
          </Button>
        }
      />
      <PersonalTokensSection onCreate={() => setCreating(true)} />
      <ConnectHelp />
      {children}
      <NewTokenDialog open={creating} onOpenChange={setCreating} />
    </div>
  );
}

/** Jak podłączyć agenta (docs/MCP_TOOLS.md §4): konektor w claude.ai (OAuth), Claude Code z tokenem, inne agenty. */
export function ConnectHelp() {
  const command = connectCommand();
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(command);
      toast.success('Polecenie skopiowane', { description: 'Wstaw swój token w miejsce <token>.' });
    } catch {
      toast.error('Nie udało się skopiować. Zaznacz tekst i skopiuj ręcznie.');
    }
  };
  return (
    <Section className="mt-6" aria-labelledby="mcp-connect">
      <SectionHeader tone="strong" title={<span id="mcp-connect">Jak podłączyć</span>} />
      <div className="flex flex-col gap-4 text-body-s text-fg-secondary">
        <div className="flex items-start gap-3">
          <Plug size={18} strokeWidth={1.75} aria-hidden className="mt-0.5 shrink-0 text-fg-muted" />
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <p>
              <span className="font-medium text-fg">Claude w przeglądarce, na telefonie i w Claude Desktop</span> —
              konektor, bez kopiowania tokenu: w claude.ai Ustawienia → Konektory → „Dodaj własny konektor” (Settings →
              Connectors → Add custom connector), adres:
            </p>
            <Mono className="rounded-lg bg-surface px-3 py-2 text-caption break-all text-fg select-all">
              {mcpBaseUrl()}/mcp
            </Mono>
            <p className="text-caption text-fg-muted">
              „Połącz” otworzy Fravity OS: zaloguj się, wybierz poziom i zakres, potwierdź hasłem. Połączenie pojawi się
              na liście wyżej — tam je odłączysz. Konektor wymaga publicznego adresu HTTPS.
            </p>
          </div>
        </div>
        <div className="flex items-start gap-3">
          <Terminal size={18} strokeWidth={1.75} aria-hidden className="mt-0.5 shrink-0 text-fg-muted" />
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <p>
              <span className="font-medium text-fg">Claude Code</span> — w terminalu, z tokenem pokazanym przy
              utworzeniu:
            </p>
            <div className="flex items-start gap-2">
              <Mono className="min-w-0 flex-1 rounded-lg bg-surface px-3 py-2 text-caption break-all text-fg select-all">
                {command}
              </Mono>
              <Button
                variant="secondary"
                icon={Copy}
                aria-label="Kopiuj polecenie"
                onClick={() => void copy()}
                className={TOUCH_ICON}
              />
            </div>
            {/* docs/MCP_TOOLS.md §4.1: zakres, sprawdzenie i zasada „token tylko w nagłówku” */}
            <ul className="flex list-disc flex-col gap-1 pl-5 text-caption text-fg-muted">
              <li>
                Token wstawiasz tylko w nagłówek <Mono>Authorization</Mono> — nigdy w adres ani do repozytorium.
              </li>
              <li>
                <Mono>-s user</Mono> doda serwer we wszystkich projektach; <Mono>-s project</Mono> zapisze{' '}
                <Mono>.mcp.json</Mono> — wtedy token przez zmienną środowiskową.
              </li>
              <li>
                Sprawdzenie: <Mono>claude mcp list</Mono> (status Connected), w sesji <Mono>/mcp</Mono>.
              </li>
            </ul>
          </div>
        </div>
        <p>
          Inne agenty zgodne z MCP (Streamable HTTP) łączą się z adresem <Mono>{mcpBaseUrl()}/mcp</Mono> i nagłówkiem{' '}
          <Mono>Authorization: Bearer &lt;token&gt;</Mono>. Claude Desktop — lokalnie przez most <Mono>mcp-remote</Mono>{' '}
          (do sprawdzenia, opis w docs/MCP_TOOLS.md).
        </p>
        <p className="text-caption text-fg-muted">
          Wysłanie maila, zmiana pamięci, faktura i wdrożenie zawsze trafiają do Akceptacji. Dane z sejfu nie są
          dostępne przez MCP. Wersja narzędzi: <Mono>{MCP_SCHEMA_VERSION}</Mono>.
        </p>
      </div>
    </Section>
  );
}
